import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, BuildingIcon, CheckIcon, KeyRoundIcon, LayersIcon, ShieldIcon, UsersIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { seededIdentities } from '../../data/auth';
import { orgRoleRegister, publicPermissionRegister, workspaceCatalogue } from '../../data/organizations';
import { runAuthorizationSuite } from '../../utils/authorizationTests';
import type { Identity } from '../../types/auth';

/**
 * The Phase 1 deliverable made inspectable.
 *
 * The conformance tab does not describe the tests — it runs them against the
 * live engine and the live store when the page renders, so what is shown is
 * what the system actually decided just now.
 */
const TABS = ['Architecture', 'Registers', 'Your access', 'Conformance', 'Migration'] as const;
type TabId = (typeof TABS)[number];
export function Architecture() {
  const [tab, setTab] = useState<TabId>('Architecture');
  const {
    store,
    memberships,
    workspaces,
    permissions,
    isInternal,
    organizations
  } = useWorkspace();
  const identities = useMemo(() => {
    const byId = (id: string) => seededIdentities.find(item => item.id === id) as Identity;
    return {
      amarachi: byId('CAMP-8H2K41QD'),
      tunde: byId('CAMP-2P9WQ7XT'),
      kelechi: byId('CAMP-5D1LM3BV')
    };
  }, []);
  const results = useMemo(() => runAuthorizationSuite({
    store,
    ...identities
  }), [store, identities]);
  const totalPassed = results.reduce((sum, group) => sum + group.passed, 0);
  const total = results.reduce((sum, group) => sum + group.total, 0);
  return <div className="space-y-5" data-id="element-5848" data-mp="urmn0lun5a1j">
      <PageHeader title="Platform architecture" description="One Campuzon identity, many memberships, many workspaces — and one authorization decision behind all of them." data-id="element-5849" data-mp="pri30luixx6k" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Architecture sections" data-id="element-5850" data-mp="3hysic9v4exbe" />

      {tab === 'Architecture' && <ModelTab data-id="element-5851" data-mp="33zj0z6v6hx5u" />}

      {tab === 'Registers' && <div className="grid gap-4 lg:grid-cols-2" data-id="element-5852" data-mp="33yrbmds6cvo0">
          <Panel title="Public roles" description="Named bundles of permissions. No role called ‘admin’ exists here — internal administration is not expressible in this register." data-id="element-5853" data-mp="38xb3svxd33ot">
            <ul className="divide-y divide-line" data-id="element-5854" data-mp="2yzoav5j89blu">
              {orgRoleRegister.map(role => <li key={role.id} className="py-3" data-id="element-5855" data-mp="2l8ruqe6hmmvo">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-5856" data-mp="2lacow2ow43t0">
                    <p className="text-body font-semibold text-ink" data-id="element-5857" data-mp="2q8nzifn0eica">{role.name}</p>
                    <Badge tone="neutral" plain data-id="element-5858" data-mp="2b9tzksek3v3p">
                      {role.appliesTo}
                    </Badge>
                    {role.canManageMembers && <Badge tone="brand" plain data-id="element-5859" data-mp="2g8nbwhi3xg2a">Can invite</Badge>}
                  </div>
                  <p className="mt-1 text-body-sm text-ink-muted" data-id="element-5860" data-mp="1woesaz4h7vw1">{role.description}</p>
                  <p className="mt-1.5 text-caption text-ink-subtle" data-id="element-5861" data-mp="1sn351092rmr0">
                    Opens: {role.workspaces.join(', ')} · {role.permissions.length} permissions
                  </p>
                </li>)}
            </ul>
          </Panel>

          <div className="space-y-4" data-id="element-5862" data-mp="2fmhivicoa0vy">
            <Panel title="Organizations" description="First-class entities. A person is never the organization." data-id="element-5863" data-mp="2flfxol1xzy0i">
              <ul className="divide-y divide-line" data-id="element-5864" data-mp="2bke68qe9oq93">
                {organizations.map(org => <li key={org.id} className="flex items-start justify-between gap-3 py-3" data-id="element-5865" data-mp="2pbsr72rqj1is">
                    <div className="min-w-0" data-id="element-5866" data-mp="2keiur35xhplg">
                      <p className="text-body font-semibold text-ink" data-id="element-5867" data-mp="2zahzc3ttx7lu">{org.name}</p>
                      <p className="text-caption text-ink-subtle" data-id="element-5868" data-mp="2z843hsrbrrd8">
                        {org.kind} · tenant {org.tenantId}
                        {org.capabilities.length > 0 && ` · ${org.capabilities.join(', ')}`}
                      </p>
                    </div>
                    <Badge tone={org.verification.state === 'verified' ? 'ok' : org.verification.state === 'rejected' ? 'danger' : 'warn'} data-id="element-5869" data-mp="wm8icwtk1teg">
                      {org.verification.state === 'verified' ? 'Verified' : 'Not verified'}
                    </Badge>
                  </li>)}
              </ul>
              <p className="mt-3 text-caption text-ink-subtle" data-id="element-5870" data-mp="iq4tlj1gad9d">
                Organization verification answers “is this entity who it says it is”. It is a
                different question from identity verification, and the two are never one flag.
              </p>
            </Panel>

            <Panel title="Public permissions" description={`${publicPermissionRegister.length} actions. Adding one is a data change.`} data-id="element-5871" data-mp="1z0gwhkvn6yd5">
              <ul className="divide-y divide-line" data-id="element-5872" data-mp="3elhrs29eckt4">
                {publicPermissionRegister.map(item => <li key={item.key} className="py-2.5" data-id="element-5873" data-mp="39tzov8ntca5i">
                    <p className="cz-num text-body-sm font-semibold text-ink" data-id="element-5874" data-mp="39sg87gy8vx37">{item.key}</p>
                    <p className="text-caption text-ink-subtle" data-id="element-5875" data-mp="34v6dpmw00v8i">{item.note}</p>
                  </li>)}
              </ul>
            </Panel>
          </div>
        </div>}

      {tab === 'Your access' && <div className="grid gap-4 lg:grid-cols-2" data-id="element-5876" data-mp="2qvnitvs77ge5">
          <Panel title="Your memberships" description="Every one, including those granting nothing — history is not erased by revocation." data-id="element-5877" data-mp="2qw6zl9643yql">
            <ul className="divide-y divide-line" data-id="element-5878" data-mp="p4acd2al10fz">
              {memberships.map(membership => {
            const org = organizations.find(item => item.id === membership.organizationId);
            const role = orgRoleRegister.find(item => item.id === membership.orgRoleId);
            return <li key={membership.id} className="py-3" data-id="element-5879" data-mp="2jcnz1p0trmu9">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-5880" data-mp="2je8urhyvlbtp">
                      <p className="text-body font-semibold text-ink" data-id="element-5881" data-mp="24oku8iyiqh71">
                        {org ? org.name : 'Personal (owned by your identity)'}
                      </p>
                      <Badge tone={membership.status === 'active' ? 'ok' : 'warn'} data-id="element-5882" data-mp="2xbno2xs923qy">
                        {membership.status}
                      </Badge>
                    </div>
                    <p className="mt-1 text-body-sm text-ink-muted" data-id="element-5883" data-mp="2ndznvsbvk41e">
                      {role?.name} · scope {membership.scope.level}
                      {membership.scope.campusId ? ` · ${membership.scope.campusId}` : ''}
                    </p>
                    {membership.statusReason && <p className="mt-1 text-caption text-warn-700" data-id="element-5884" data-mp="1gy6xwg8whw6n">{membership.statusReason}</p>}
                  </li>;
          })}
              {memberships.length === 0 && <li className="py-3 text-body-sm text-ink-muted" data-id="element-5885" data-mp="3q5adooojp24f">
                  This identity holds no membership, so it opens no workspace.
                </li>}
            </ul>
          </Panel>

          <div className="space-y-4" data-id="element-5886" data-mp="3zn6rn328ybj">
            <Panel title="Workspaces you may enter" description="Derived from memberships on every read. Never stored." data-id="element-5887" data-mp="3ceyhoeouaj80">
              <ul className="divide-y divide-line" data-id="element-5888" data-mp="37gdrl83axfxa">
                {workspaces.map(space => <li key={space.id} className="flex items-start justify-between gap-3 py-3" data-id="element-5889" data-mp="3l7sdpnsi9x8k">
                    <div className="min-w-0" data-id="element-5890" data-mp="3gg0k14e1zbe7">
                      <p className="text-body font-semibold text-ink" data-id="element-5891" data-mp="3gez52otx24r7">{space.label}</p>
                      <p className="text-caption text-ink-subtle" data-id="element-5892" data-mp="2spmzdyxssetn">
                        {space.ownerLabel} · {space.roleName} · {space.path}
                      </p>
                    </div>
                    <Badge tone="ok" data-id="element-5893" data-mp="3dlgku63hacxw">Authorised</Badge>
                  </li>)}
              </ul>
              <p className="mt-3 text-caption text-ink-subtle" data-id="element-5894" data-mp="3ijrt4ce4kfe0">
                {workspaceCatalogue.length - workspaces.length} other workspace types exist and are
                refused for this identity. Selecting one is not a way of being granted it.
              </p>
            </Panel>

            <Panel title="Internal administration" description="A separate authorization domain." data-id="element-5895" data-mp="d3sayq3yee5">
              <div className="flex items-start gap-3" data-id="element-5896" data-mp="5b59fe3z43mz">
                <ShieldIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" data-id="element-5897" data-mp="a3ojw27d6ink" />
                <div data-id="element-5898" data-mp="e4s410wg30ul">
                  <p className="text-body text-ink" data-id="element-5899" data-mp="e59wrnjwewxr">
                    {isInternal ? 'This identity has a provisioned internal staff link.' : 'This identity has no internal staff link and cannot reach /admin.'}
                  </p>
                  <p className="mt-1.5 text-body-sm text-ink-muted" data-id="element-5900" data-mp="y6v1wpmispml">
                    Internal access comes only from a provisioned link table. There is no public
                    role, no signup option and no request shape that can create one.
                  </p>
                </div>
              </div>
            </Panel>

            <Panel title="Effective permissions" description="The union of what your active memberships carry." data-id="element-5901" data-mp="k8oksl5sj49m">
              <ul className="flex flex-wrap gap-1.5" data-id="element-5902" data-mp="1gx4dur83zvrh">
                {permissions.map(key => <li key={key} className="cz-num rounded-md bg-canvas px-2 py-1 text-caption text-ink-muted" data-id="element-5903" data-mp="1bz2q3szqn89o">
                    {key}
                  </li>)}
              </ul>
            </Panel>
          </div>
        </div>}

      {tab === 'Conformance' && <div className="space-y-4" data-id="element-5904" data-mp="1uxnf9fi2k3ko">
          <Panel title="Authorization conformance" description="Executed against the live engine and the live store when this page rendered." data-id="element-5905" data-mp="1uwltafzilwo6">
            <div className="flex flex-wrap items-center gap-3" data-id="element-5906" data-mp="2pgexkvzck1by">
              <p className="cz-num text-h2 font-bold text-ink" data-id="element-5907" data-mp="2phzombmgd252">
                {totalPassed}
                <span className="text-h4 font-semibold text-ink-subtle" data-id="element-5908" data-mp="33d1w336y6zjz"> / {total}</span>
              </p>
              <Badge tone={totalPassed === total ? 'ok' : 'danger'} data-id="element-5909" data-mp="3ddm18gxl8o7h">
                {totalPassed === total ? 'All cases pass' : `${total - totalPassed} failing`}
              </Badge>
            </div>
            <p className="mt-3 max-w-[70ch] text-body-sm text-ink-muted" data-id="element-5910" data-mp="3m5w9vtsu4lmm">
              Most of these are deliberate bypass attempts — forged roles, borrowed organization
              ids, revoked memberships, another institution’s campus. A case passes only when the
              engine refuses for the <em data-id="element-5911" data-mp="3w1r29gmv1v2l">right</em> reason, so a change that starts denying
              everything cannot turn the suite green.
            </p>
          </Panel>

          {results.map(group => <Panel key={group.group} title={group.group} description={`${group.passed} of ${group.total} passing`} data-id="element-5912" data-mp="ejk0vo0riptq">
              <ul className="divide-y divide-line" data-id="element-5913" data-mp="4mp0t0ouchdc">
                {group.cases.map(item => <li key={item.id} className="flex items-start gap-3 py-3" data-id="element-5914" data-mp="9kqltr5tuqrg">
                    <span aria-hidden className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${item.passed ? 'bg-ok-50 text-ok-700' : 'bg-danger-50 text-danger-700'}`} data-id="element-5915" data-mp="3rz9jtxo57ko8">
                      {item.passed ? <CheckIcon className="h-3.5 w-3.5" data-id="element-5916" data-mp="3myvfaplr2suy" /> : <XIcon className="h-3.5 w-3.5" data-id="element-5917" data-mp="393jfi3sqmc76" />}
                    </span>
                    <div className="min-w-0" data-id="element-5918" data-mp="3d4uy3pd71x5v">
                      <p className="text-body font-semibold text-ink" data-id="element-5919" data-mp="1jlcuahi3v4sk">{item.name}</p>
                      <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-5920" data-mp="1et30urhs7mlj">{item.intent}</p>
                      <p className="cz-num mt-1 text-caption text-ink-subtle" data-id="element-5921" data-mp="19u8e5egp1dw5">
                        expected <span className="text-ink-muted" data-id="element-5922" data-mp="14vez3hlrdetv">{item.expected}</span> · got{' '}
                        <span className={item.passed ? 'text-ink-muted' : 'text-danger-700'} data-id="element-5923" data-mp="22j5mopv3rwbp">
                          {item.actual}
                        </span>
                      </p>
                    </div>
                  </li>)}
              </ul>
            </Panel>)}
        </div>}

      {tab === 'Migration' && <MigrationTab data-id="element-5924" data-mp="1oimgzpe412cp" />}
    </div>;
}

/* ------------------------------------------------------------------ model */

const LAYERS = [{
  icon: KeyRoundIcon,
  title: 'Identity',
  answer: 'Who is this person?',
  detail: 'One account, one credential, one session, one email-verification state. Reused as it was — no second authentication system was introduced.',
  where: 'contexts/AuthContext.tsx · types/auth.ts'
}, {
  icon: BuildingIcon,
  title: 'Organization',
  answer: 'Which entity do they participate in?',
  detail: 'Institutions, providers, employers and communities are records with their own tenant, verification and capabilities. A person is never the organization.',
  where: 'data/organizations.ts · types/workspace.ts'
}, {
  icon: UsersIcon,
  title: 'Membership',
  answer: 'Are they authorised to participate, and where?',
  detail: 'Invited, pending, active, suspended, revoked, declined. Only active grants anything; the rest are kept because history is not access.',
  where: 'types/workspace.ts'
}, {
  icon: LayersIcon,
  title: 'Role → Workspace',
  answer: 'In what capacity, and which environment does it open?',
  detail: 'A role is a bundle of permissions plus the workspaces it opens. Workspaces are derived from memberships on every read, so selecting one can never grant it.',
  where: 'utils/authorization.ts'
}, {
  icon: ShieldIcon,
  title: 'Permission → Resource scope',
  answer: 'May they do this, to this record?',
  detail: 'Default deny. The resource reference is a claim that gets validated, so naming another organization’s id reaches nothing.',
  where: 'utils/authorization.ts'
}];
function ModelTab() {
  return <div className="grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" data-id="element-5925" data-mp="84l75joxdasu">
      <Panel title="The decision chain" description="Every protected question passes through all five, in order. A failure at any layer ends it." data-id="element-5926" data-mp="mszadvq9719x">
        <ol className="space-y-1" data-id="element-5927" data-mp="i1h7v38l0smu">
          {LAYERS.map(({
          icon: Icon,
          ...layer
        }, index) => <li key={layer.title} className="flex gap-3 py-2.5" data-id="element-5928" data-mp="3qgrhcwesx04n" data-mpi={index}>
              <div className="flex flex-col items-center" data-id="element-5929" data-mp="3ljhjqth9di5q">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-800" data-id="element-5930" data-mp="3lgvdcx63t8p3">
                  <Icon aria-hidden className="h-5 w-5" data-id="element-5931" data-mp="25rnf6obbljrz" />
                </span>
                {index < LAYERS.length - 1 && <span className="mt-1 w-px flex-1 bg-line" data-id="element-5932" data-mp="1r3110pxfiy2c" />}
              </div>
              <div className="min-w-0 pb-2" data-id="element-5933" data-mp="1r0wpbltaguri">
                <p className="text-body font-semibold text-ink" data-id="element-5934" data-mp="1w2vqqq5gxhlt">{layer.title}</p>
                <p className="text-body-sm font-medium text-brand-800" data-id="element-5935" data-mp="2jqwrhcwentqo">{layer.answer}</p>
                <p className="mt-1 max-w-[62ch] text-body-sm leading-relaxed text-ink-muted" data-id="element-5936" data-mp="2oq9jec24vr04">
                  {layer.detail}
                </p>
                <p className="cz-num mt-1 text-caption text-ink-subtle" data-id="element-5937" data-mp="2aq6zkka9mni8">{layer.where}</p>
              </div>
            </li>)}
        </ol>
      </Panel>

      <div className="space-y-4" data-id="element-5938" data-mp="zb1uxaqkqevz">
        <Panel title="One person, several participations" description="The case the architecture exists for." data-id="element-5939" data-mp="za0bahvhsk2n">
          <pre className="cz-num overflow-x-auto rounded-lg bg-canvas p-3 text-caption leading-relaxed text-ink-muted" data-id="element-5940" data-mp="13ehp80f6835i">
{`Amarachi Obi
  └── ONE Campuzon identity
        ├── personal membership
        │     └── Student workspace
        ├── Amarachi Foods · Provider Owner
        │     └── Provider workspace
        └── Engineering Society · Community Manager
              └── Community workspace

one password · one session · one verification`}
          </pre>
        </Panel>

        <Panel title="Two domains, deliberately apart" data-id="element-5941" data-mp="2naxc6foqakr1">
          <div className="space-y-3" data-id="element-5942" data-mp="2i9q38daunfis">
            <div className="rounded-lg border border-line p-3" data-id="element-5943" data-mp="2i8oex74towj4">
              <p className="text-body-sm font-semibold text-ink" data-id="element-5944" data-mp="2damxeqn1p59r">Public</p>
              <p className="mt-1 text-body-sm text-ink-muted" data-id="element-5945" data-mp="28c39wlsszlfa">
                Identity → membership → role → workspace → permission. Anyone can hold these.
              </p>
            </div>
            <div className="rounded-lg border border-danger-100 bg-danger-50 p-3" data-id="element-5946" data-mp="bhfi3h7fmnnv">
              <p className="text-body-sm font-semibold text-ink" data-id="element-5947" data-mp="6im47no8gun6">Internal</p>
              <p className="mt-1 text-body-sm text-ink-muted" data-id="element-5948" data-mp="f0xrqh8fovw9">
                Provisioned staff link → internal role → internal permission → /admin. Not a public
                role, not selectable at signup, not reachable by holding any membership.
              </p>
            </div>
          </div>
        </Panel>

        <Panel title="What is never trusted" data-id="element-5949" data-mp="ssccashzp55f">
          <ul className="space-y-2" data-id="element-5950" data-mp="3s8r4oejmmi55">
            {['A role on the identity object', 'An organization id in the request', 'A workspace id in the URL', 'A user id in the request', 'Anything stored on the device', 'Whether the interface showed a button'].map(item => <li key={item} className="flex items-start gap-2 text-body-sm text-ink-muted" data-id="element-5951" data-mp="a09vnh5clov6">
                <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warn-600" data-id="element-5952" data-mp="1gl2nv0lzdmf6" />
                {item}
              </li>)}
          </ul>
          <p className="mt-3 text-caption text-ink-subtle" data-id="element-5953" data-mp="26mdddm66l7t1">
            Hiding a control is presentation. Every one of these is re-resolved from the store
            against the authenticated identity before anything is allowed.
          </p>
        </Panel>
      </div>
    </div>;
}

/* -------------------------------------------------------------- migration */

const MIGRATION = [{
  verdict: 'Reused',
  tone: 'ok' as const,
  items: ['AuthContext — the only authentication system. No second provider introduced.', 'Identity, credentials, salted digest, login throttle, account standing.', 'Sessions, restore-on-refresh, sign out, sign out everywhere.', 'Email verification and password reset, both at identity level.', 'Username uniqueness, reserved names, 30-day interval, history.', 'Security event log, and the governance engine for internal staff.', 'RequireAuth, and the captured post-signin destination.']
}, {
  verdict: 'Refactored',
  tone: 'warn' as const,
  items: ['Console switching moved off identity.roles onto resolved workspaces, so it lists what the store authorises rather than what a role name implies.', 'Operator routes now sit behind a workspace decision instead of only “is signed in”.', '/admin moved behind the provisioned internal staff link. It was previously reachable by any authenticated account.', 'The no-access page now names which authorization was missing.']
}, {
  verdict: 'Created',
  tone: 'brand' as const,
  items: ['Organization, Membership, OrgRole, Workspace, Invitation, InternalStaffLink.', 'The public permission register and the role register.', 'authorize() — one default-deny decision with resource and scope checks.', 'WorkspaceProvider, RequireWorkspace, RequireInternal.', 'The authorization conformance suite.']
}, {
  verdict: 'Deliberately not done',
  tone: 'neutral' as const,
  items: ['No dashboards were built or redesigned — Phase 3.', 'No signup or login redesign — Phase 2.', 'No homepage changes.', 'Existing seeded operator datasets were left in place. They are not yet keyed by organization; each console still reads its own seed. Phase 3 attaches ownerOrgId to those records so the engine can scope them.']
}];
function MigrationTab() {
  return <div className="grid gap-4 lg:grid-cols-2" data-id="element-5954" data-mp="3l4hnjs0te0n6">
      {MIGRATION.map(section => <Panel key={section.verdict} title={section.verdict} data-id="element-5955" data-mp="3q55fqh0s4niz">
          <ul className="space-y-2.5" data-id="element-5956" data-mp="3b96817hl0ped">
            {section.items.map(item => <li key={item} className="flex items-start gap-2.5 text-body-sm leading-relaxed text-ink-muted" data-id="element-5957" data-mp="3g77snwunowry">
                <Badge tone={section.tone} plain data-id="element-5958" data-mp="1zsycvr4hcgcq">
                  ·
                </Badge>
                <span className="min-w-0" data-id="element-5959" data-mp="24su7x0v78xg5">{item}</span>
              </li>)}
          </ul>
        </Panel>)}
      <Panel title="Nothing was destroyed" description="No existing model was replaced." data-id="element-5960" data-mp="mted9v8x6fg5">
        <p className="max-w-[70ch] text-body-sm leading-relaxed text-ink-muted" data-id="element-5961" data-mp="hujl42p2bejd">
          The authorization layer was added <em data-id="element-5962" data-mp="vuubz5qdgt4j">beside</em> the existing identity system rather than
          over it. Every seeded account, session, verification state and route continues to work,
          and the previous role list still exists on the identity — it is simply no longer the thing
          that decides access. That leaves a real migration path: as each console's records gain an
          owning organization, its authorization moves from the role field onto the engine without a
          second rewrite.
        </p>
      </Panel>
    </div>;
}